import { ColumnDef, createColumnHelper, Row } from '@tanstack/react-table';
import { parseISO } from 'date-fns';
import {
  CalendarClockIcon,
  SquareMinusIcon,
  SquarePlusIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from 'lucide-react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { ScrollableCellWithAvatar } from '@/components/data-table/cells';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import i18n from '@/core/i18n';
import {
  calculateWeightedAverageRate,
  formatTrimmedDecimal,
  parseFormNumber,
  roundToPrecision,
} from '@/helpers/finance';
import { cn } from '@/lib/utils';
import { DecimalFormField, InputFieldWithCurrency, SingleSelect } from '@/modules/common';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { SingleSelectWithPagination } from '@/modules/common/components/molecules/selectors/single-select/single-select-with-pagination';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import {
  createScrollableOptionRenderer,
  createScrollableTriggerRenderer,
  ExpandAllRowsButton,
  hasBlockingQuickBooksWarnings,
  QuickBooksWarningsIndicator,
} from '@/modules/finance/common';
import {
  FINANCE_LINE_DESCRIPTION_MAX_LENGTH,
  FINANCE_QUANTITY_MAX_DECIMAL_PLACES,
} from '@/modules/finance/common/constants/line-item-validation.constants';
import {
  FINANCE_MAX_INTEGER_DIGITS,
  FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES,
  FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES,
} from '@/modules/finance/common/constants/rounding.constants';
import { ScrollableText } from '@/modules/planner/components/assignment/text/scrollable-text';
import { ProjectBillingModel } from '@/modules/projects';
import { getBillingModelLabel } from '@/modules/projects/utils/billing-model.utils';
import { formatDisplayDate } from '@/utils/date-formatters';

import { AddInvoiceLineButton } from '../components/add-invoice-line-button';
import {
  PaymentDetailsCell,
  PaymentDetailsSelector,
} from '../components/payment-details/payment-details-cell';
import {
  GenerateInvoiceAddedMemberListItem,
  GenerateInvoiceAddOverheadButtonRow,
  GenerateInvoiceListItem,
  GenerateInvoiceProjectOverheadListItem,
  GenerateInvoiceProjectTeamMemberListItem,
  GenerateInvoiceSubRow,
  GenerateInvoiceSubRowType,
  InvoiceHoursSource,
  InvoiceHoursSourceType,
} from '../types';
import { QuickBooksProjectWarningTypeDTO } from '../types/dto/response/quick-books-project-warning.response';
import {
  isInvoiceLineDescriptionEmpty,
  isInvoiceLineDescriptionTooLong,
  isInvoiceMemberLineZero,
} from '../validations/invoice-item-validation';

const columnHelper = createColumnHelper<GenerateInvoiceListItem>();

const renderMemberOption = createScrollableOptionRenderer();
const renderMemberTrigger = createScrollableTriggerRenderer();

// Sub-row type helpers
const isSubRow = (row: Row<GenerateInvoiceListItem>) => row.depth === 1;
const getSubRowType = (row: Row<GenerateInvoiceListItem>): GenerateInvoiceSubRowType =>
  (row.original as unknown as GenerateInvoiceSubRow).type;
const isMemberSubRow = (row: Row<GenerateInvoiceListItem>) =>
  isSubRow(row) && getSubRowType(row) === GenerateInvoiceSubRowType.MEMBER;
const isAddedMemberSubRow = (row: Row<GenerateInvoiceListItem>) =>
  isSubRow(row) && getSubRowType(row) === GenerateInvoiceSubRowType.ADDED_MEMBER;
const isOverheadSubRow = (row: Row<GenerateInvoiceListItem>) =>
  isSubRow(row) && getSubRowType(row) === GenerateInvoiceSubRowType.OVERHEAD;
const isAddOverheadSubRow = (row: Row<GenerateInvoiceListItem>) =>
  isSubRow(row) && getSubRowType(row) === GenerateInvoiceSubRowType.ADD_OVERHEAD;

/**
 * Field names are keyed by STABLE ENTITY IDS, not array index, so edits survive filter
 * changes (a filter that re-shows an entity resolves the same path). Segments are prefixed
 * (`p`/`m`/`o`/`a`) so RHF treats them as object keys — a bare numeric id would be read as an
 * array index and land in the wrong (sparse) slot. See `getInvoiceProjectPath` for the base.
 */
export const getInvoiceProjectPath = (projectId: number) => `project.p${projectId}` as const;

export const getInvoiceFieldName = (row: Row<GenerateInvoiceListItem>, name: string) => {
  const parentRow = row.getParentRow();
  if (!parentRow) return `__noop__.${name}`;

  const base = getInvoiceProjectPath(parentRow.original.project.id);
  const subRow = row.original as unknown as GenerateInvoiceSubRow;
  switch (subRow.type) {
    case GenerateInvoiceSubRowType.OVERHEAD:
      return `${base}.overhead.o${subRow.id}.${name}`;
    case GenerateInvoiceSubRowType.ADDED_MEMBER:
      return `${base}.addedMember.a${subRow.id}.${name}`;
    case GenerateInvoiceSubRowType.MEMBER:
      return `${base}.member.m${subRow.id}.${name}`;
    default:
      return `__noop__.${name}`;
  }
};

const isReadOnly = (row: Row<GenerateInvoiceListItem>) => !row.getIsSelected();
const hasQuickBooksError = (row: Row<GenerateInvoiceListItem> | undefined) =>
  row && hasBlockingQuickBooksWarnings(row.original.project.warnings);
const isRowDisabled = (row: Row<GenerateInvoiceListItem>) =>
  isSubRow(row) ? hasQuickBooksError(row.getParentRow()) : hasQuickBooksError(row);
const hasSubRows = (row: Row<GenerateInvoiceListItem>) =>
  row.subRows.some(sr => !isAddOverheadSubRow(sr));

export interface OverheadActions {
  addOverhead: (projectId: string, currencyCode: string) => void;
  removeOverhead: (projectId: string, overheadId: string) => void;
}

export interface AddedMemberActions {
  addMember: (projectId: string, currencyCode: string) => void;
  removeAddedMember: (projectId: string, addedMemberId: string) => void;
}

/** Paginated server-side loaders for the "Add custom member" picker. */
export interface AddedMemberSelector {
  loadItems: (cursor?: number | null) => Promise<PaginatedResponse<DropdownSearchItem>>;
  searchItems: (
    searchTerm: string,
    cursor?: number | null
  ) => Promise<PaginatedResponse<DropdownSearchItem>>;
}

const ADDED_MEMBER_SELECTOR_QUERY_KEY = ['generate-invoices-added-member-options'];

const AddOverheadButton = ({
  row,
  overheadActions,
  addedMemberActions,
}: {
  row: Row<GenerateInvoiceListItem>;
  overheadActions: OverheadActions;
  addedMemberActions: AddedMemberActions;
}) => {
  const data = row.original as unknown as GenerateInvoiceAddOverheadButtonRow;

  return (
    <div className="flex items-center py-3 pl-10">
      <AddInvoiceLineButton
        label={i18n.t('authenticated.invoices:new.overhead.addCustomLine')}
        onAddOverhead={() => overheadActions.addOverhead(data.projectId, data.currencyCode)}
        onAddMember={() => addedMemberActions.addMember(data.projectId, data.currencyCode)}
      />
    </div>
  );
};

const OverheadTotal = ({ row }: { row: Row<GenerateInvoiceListItem> }) => {
  const formContext = useFormContext();
  const rate = useWatch({
    control: formContext.control,
    name: getInvoiceFieldName(row, 'effectiveRate'),
  });
  const total = roundToPrecision(parseFormNumber(rate), FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES);
  const currencyCode = (row.original as unknown as GenerateInvoiceProjectOverheadListItem).currency
    .code;

  return (
    <div className="flex justify-between">
      <span>{formatTrimmedDecimal(total, FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES)}</span>
      <span className="text-muted-foreground">{currencyCode}</span>
    </div>
  );
};

const ProjectTotalHours = ({ row }: { row: Row<GenerateInvoiceListItem> }) => {
  const formContext = useFormContext();
  const dataSubRows = row.subRows.filter(sr => !isAddOverheadSubRow(sr));
  const fieldPaths = dataSubRows.map(subRow => getInvoiceFieldName(subRow, 'hours'));

  const hoursValues = useWatch({
    control: formContext.control,
    name: fieldPaths,
  });

  const totalHours = dataSubRows.reduce((sum, subRow, idx) => {
    if (!subRow.getIsSelected()) return sum;
    return sum + parseFormNumber(hoursValues?.[idx] ?? subRow.original.hours);
  }, 0);

  return <span className="font-semibold">{formatTrimmedDecimal(totalHours)}</span>;
};

const ProjectAverageRate = ({
  row,
  currencyCode,
}: {
  row: Row<GenerateInvoiceListItem>;
  currencyCode: string;
}) => {
  const formContext = useFormContext();
  const dataSubRows = row.subRows.filter(sr => !isAddOverheadSubRow(sr));

  const hoursFieldPaths = dataSubRows.map(subRow => getInvoiceFieldName(subRow, 'hours'));
  const rateFieldPaths = dataSubRows.map(subRow => getInvoiceFieldName(subRow, 'effectiveRate'));

  const hoursValues = useWatch({ control: formContext.control, name: hoursFieldPaths });
  const rateValues = useWatch({ control: formContext.control, name: rateFieldPaths });

  // Read watched values at the row's original `dataSubRows` index — filtering
  // before mapping would re-index against the filtered array and pull the wrong
  // member's hours/rate (OPS-1584).
  const entries = dataSubRows.reduce<{ hours: number; rate: number }[]>((acc, subRow, idx) => {
    if (!subRow.getIsSelected()) return acc;
    acc.push({
      hours: parseFormNumber(hoursValues?.[idx] ?? subRow.original.hours),
      rate: parseFormNumber(rateValues?.[idx] ?? subRow.original.effectiveRate),
    });
    return acc;
  }, []);

  const { averageRate: displayRate } = calculateWeightedAverageRate(entries);

  return (
    <div className="flex justify-between">
      <span className="font-semibold">{formatTrimmedDecimal(displayRate)}</span>
      <span className="text-muted-foreground">
        {i18n.t('common:rateUnits.perHour', {
          currency: currencyCode,
        })}
      </span>
    </div>
  );
};

const ProjectTotalAmount = ({
  row,
  currencyCode,
}: {
  row: Row<GenerateInvoiceListItem>;
  currencyCode: string;
}) => {
  const formContext = useFormContext();
  const dataSubRows = row.subRows.filter(sr => !isAddOverheadSubRow(sr));

  const hoursFieldPaths = dataSubRows.map(subRow => getInvoiceFieldName(subRow, 'hours'));
  const rateFieldPaths = dataSubRows.map(subRow => getInvoiceFieldName(subRow, 'effectiveRate'));

  const hoursValues = useWatch({ control: formContext.control, name: hoursFieldPaths });
  const rateValues = useWatch({ control: formContext.control, name: rateFieldPaths });

  let totalAmount = 0;

  dataSubRows.forEach((subRow, idx) => {
    if (!subRow.getIsSelected()) return;

    const subRowType = (subRow.original as unknown as GenerateInvoiceSubRow).type;

    if (subRowType === GenerateInvoiceSubRowType.OVERHEAD) {
      totalAmount += parseFormNumber(rateValues?.[idx]);
    } else if (
      subRowType === GenerateInvoiceSubRowType.MEMBER ||
      subRowType === GenerateInvoiceSubRowType.ADDED_MEMBER
    ) {
      const hours = parseFormNumber(hoursValues?.[idx] ?? subRow.original.hours);
      const rate = parseFormNumber(rateValues?.[idx] ?? subRow.original.effectiveRate);
      totalAmount += hours * rate;
    }
  });

  return (
    <div className="flex justify-between">
      <span className="font-semibold">
        {formatTrimmedDecimal(totalAmount, FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES)}
      </span>
      <span className="text-muted-foreground">{currencyCode}</span>
    </div>
  );
};

const MemberTotal = ({
  row,
  currencyCode,
}: {
  row: Row<GenerateInvoiceListItem>;
  currencyCode: string;
}) => {
  const formContext = useFormContext();

  const hours = useWatch({ control: formContext.control, name: getInvoiceFieldName(row, 'hours') });
  const rate = useWatch({
    control: formContext.control,
    name: getInvoiceFieldName(row, 'effectiveRate'),
  });

  const hoursNum = parseFormNumber(hours);
  const rateNum = parseFormNumber(rate);
  const calculatedTotal = roundToPrecision(
    hoursNum * rateNum,
    FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES
  );

  return (
    <div className="flex justify-between">
      <span>{formatTrimmedDecimal(calculatedTotal, FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES)}</span>
      <span className="text-muted-foreground">{currencyCode}</span>
    </div>
  );
};

const useHasZeroAmountError = (row: Row<GenerateInvoiceListItem>) => {
  const formContext = useFormContext();

  // `useWatch`, not `watch()`: `watch()` re-renders the form ROOT (the whole page and table) on
  // every keystroke; `useWatch` re-renders only this cell.
  const hours = useWatch({ control: formContext.control, name: getInvoiceFieldName(row, 'hours') });
  const rate = useWatch({
    control: formContext.control,
    name: getInvoiceFieldName(row, 'effectiveRate'),
  });

  const isSelected = row.getIsSelected();
  return (
    isSelected &&
    isInvoiceMemberLineZero({
      quantity: parseFormNumber(hours),
      unitPrice: parseFormNumber(rate),
    })
  );
};

const MemberHoursInput = ({ row, form }: { row: Row<GenerateInvoiceListItem>; form: any }) => {
  const fieldName = getInvoiceFieldName(row, 'hours');
  const hasZeroError = useHasZeroAmountError(row);

  return (
    <DecimalFormField
      control={form.control}
      name={fieldName}
      disabled={isReadOnly(row)}
      className={hasZeroError ? 'border-destructive' : ''}
      maxDecimalPlaces={FINANCE_QUANTITY_MAX_DECIMAL_PLACES}
      maxIntegerDigits={FINANCE_MAX_INTEGER_DIGITS}
    />
  );
};

const MemberRateCell = ({ row, form }: { row: Row<GenerateInvoiceListItem>; form: any }) => {
  const hasZeroError = useHasZeroAmountError(row);

  return (
    <div className={hasZeroError ? '[&_input]:border-destructive' : ''}>
      <InputFieldWithCurrency
        name={getInvoiceFieldName(row, 'effectiveRate')}
        isReadMode={isReadOnly(row)}
        control={form.control}
        suffixText={i18n.t('common:rateUnits.perHour', {
          currency: row.original.currency.code,
        })}
        maxDecimalPlaces={FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES}
        maxIntegerDigits={FINANCE_MAX_INTEGER_DIGITS}
      />
    </div>
  );
};

const ProjectZeroAmountWarning = ({ row }: { row: Row<GenerateInvoiceListItem> }) => {
  const formContext = useFormContext();

  // Project-level validation issues drive this warning:
  //   - member / added-member rows whose hours and rate are both 0
  //   - overhead rows whose description is empty or too long
  //   - added-member rows that haven't picked a memberId yet
  // Overhead rows are excluded from the zero-amount check because they
  // display a fixed "1 hour" and intentionally only carry a rate.
  const numericSubRows = row.subRows.filter(
    sr => !isAddOverheadSubRow(sr) && !isOverheadSubRow(sr)
  );
  const hoursFieldPaths = numericSubRows.map(subRow => getInvoiceFieldName(subRow, 'hours'));
  const rateFieldPaths = numericSubRows.map(subRow => getInvoiceFieldName(subRow, 'effectiveRate'));

  const overheadSubRows = row.subRows.filter(sr => isOverheadSubRow(sr));
  const overheadDescriptionPaths = overheadSubRows.map(sr =>
    getInvoiceFieldName(sr, 'description')
  );

  const addedMemberSubRows = row.subRows.filter(sr => isAddedMemberSubRow(sr));
  const addedMemberIdPaths = addedMemberSubRows.map(sr => getInvoiceFieldName(sr, 'memberId'));

  const hoursValues = useWatch({ control: formContext.control, name: hoursFieldPaths });
  const rateValues = useWatch({ control: formContext.control, name: rateFieldPaths });
  const descriptionValues = useWatch({
    control: formContext.control,
    name: overheadDescriptionPaths,
  });
  const memberIdValues = useWatch({ control: formContext.control, name: addedMemberIdPaths });

  const hasZeroAmountSelected = numericSubRows.some((subRow, idx) => {
    if (!subRow.getIsSelected()) return false;
    const hours = parseFormNumber(hoursValues?.[idx] ?? subRow.original.hours);
    const rate = parseFormNumber(rateValues?.[idx] ?? subRow.original.effectiveRate);
    return isInvoiceMemberLineZero({ quantity: hours, unitPrice: rate });
  });

  const hasEmptyOverheadName = overheadSubRows.some((subRow, idx) => {
    if (!subRow.getIsSelected()) return false;
    const overhead = subRow.original as unknown as GenerateInvoiceProjectOverheadListItem;
    const description = String(descriptionValues?.[idx] ?? overhead.description ?? '');
    return isInvoiceLineDescriptionEmpty(description);
  });

  const hasLongOverheadDescription = overheadSubRows.some((subRow, idx) => {
    if (!subRow.getIsSelected()) return false;
    const overhead = subRow.original as unknown as GenerateInvoiceProjectOverheadListItem;
    const description = String(descriptionValues?.[idx] ?? overhead.description ?? '');
    return isInvoiceLineDescriptionTooLong(description);
  });

  const hasMissingMember = addedMemberSubRows.some((subRow, idx) => {
    if (!subRow.getIsSelected()) return false;
    const added = subRow.original as unknown as GenerateInvoiceAddedMemberListItem;
    const memberId = memberIdValues?.[idx] ?? added.memberId;
    return memberId === null || memberId === undefined || memberId === '';
  });

  if (
    !hasZeroAmountSelected &&
    !hasEmptyOverheadName &&
    !hasLongOverheadDescription &&
    !hasMissingMember
  )
    return null;

  const tooltipMessages: string[] = [];
  if (hasZeroAmountSelected) {
    tooltipMessages.push(i18n.t('authenticated.invoices:new.validation.zeroAmountWarning'));
  }
  if (hasEmptyOverheadName) {
    tooltipMessages.push(i18n.t('authenticated.invoices:new.validation.emptyOverheadNameWarning'));
  }
  if (hasLongOverheadDescription) {
    tooltipMessages.push(
      i18n.t('authenticated.invoices:details.edit.validation.descriptionTooLong')
    );
  }
  if (hasMissingMember) {
    tooltipMessages.push(i18n.t('authenticated.invoices:new.validation.missingMemberWarning'));
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <TriangleAlertIcon size={16} className="shrink-0 text-destructive" />
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent>
          <div className="flex flex-col gap-1">
            {tooltipMessages.map(msg => (
              <span key={msg}>{msg}</span>
            ))}
          </div>
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  );
};

const buildQuickBooksWarningMessages = (project: GenerateInvoiceListItem['project']): string[] =>
  project.warnings.map(warning => {
    const args =
      warning.type === QuickBooksProjectWarningTypeDTO.PROJECT_CURRENCY_MISMATCHES_QUICK_BOOKS &&
      warning.args
        ? {
            ...warning.args,
            endOfRequestedPeriod: formatDisplayDate(String(warning.args.endOfRequestedPeriod)),
          }
        : warning.args;
    return i18n.t(`authenticated.invoices:modals.quickbooksWarning.warnings.${warning.type}`, args);
  });

const MemberZeroAmountError = ({ row }: { row: Row<GenerateInvoiceListItem> }) => {
  const hasZeroError = useHasZeroAmountError(row);

  if (!hasZeroError) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <TriangleAlertIcon size={16} className="shrink-0 text-destructive" />
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent>
          {i18n.t('authenticated.invoices:new.validation.zeroAmountMemberError')}
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  );
};

const BILLING_MODEL_BADGE_CLASSES: Record<ProjectBillingModel, string> = {
  [ProjectBillingModel.TM]:
    'border-violet-500 bg-violet-500/10 text-violet-600 dark:text-violet-400',
  [ProjectBillingModel.FixedPrice]: 'border-warning bg-warning/10 text-warning',
  [ProjectBillingModel.NonBillable]: 'border-transparent bg-secondary text-secondary-foreground',
};

const DISABLED_ROW_CLASS = 'opacity-50';

interface ProjectCellProps {
  projectName: string;
  billingModel: ProjectBillingModel | null;
  clientName: string | null;
  colorHex: string;
  disabled: boolean;
}

const ProjectCell = ({
  projectName,
  billingModel,
  clientName,
  colorHex,
  disabled,
}: ProjectCellProps) => (
  <div className={cn('flex min-w-0 flex-1 items-center gap-2', disabled && DISABLED_ROW_CLASS)}>
    {colorHex && (
      <div className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: colorHex }} />
    )}
    <div className="flex min-w-0 flex-1 flex-col">
      <div className="flex min-w-0 items-center gap-1.5">
        <div className="min-w-0 overflow-hidden">
          <ScrollableText className="whitespace-nowrap">{projectName}</ScrollableText>
        </div>
        {billingModel && (
          <Badge
            variant="outline"
            className={cn('shrink-0', BILLING_MODEL_BADGE_CLASSES[billingModel])}
          >
            {getBillingModelLabel(billingModel)}
          </Badge>
        )}
      </div>
      {clientName && <span className="truncate text-xs text-muted-foreground">{clientName}</span>}
    </div>
  </div>
);

export const getGenerateInvoicesTableColumns = (
  form,
  overheadActions: OverheadActions,
  addedMemberActions: AddedMemberActions,
  addedMemberSelector: AddedMemberSelector,
  paymentDetailsSelector: PaymentDetailsSelector | null
): ColumnDef<GenerateInvoiceListItem>[] => [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <label className="flex cursor-pointer items-center justify-center px-3 py-3">
        <Checkbox
          checked={
            table.getIsAllRowsSelected()
              ? true
              : table.getIsSomeRowsSelected()
                ? 'indeterminate'
                : false
          }
          onCheckedChange={value => {
            table.getCoreRowModel().rows.forEach(row => {
              // if checkbox indeterminate unsellect it and all subRows
              if (table.getIsSomeRowsSelected()) {
                row.toggleSelected(false);
                row.subRows.forEach(subRow => subRow.toggleSelected(false));
                return;
              }
              if (
                // we can define the enableRowSelect dunction in the useTable hook
                // but it didn't work, so I hardcoded check here
                !hasBlockingQuickBooksWarnings(row.original.project.warnings)
              ) {
                row.toggleSelected(!!value);
              }
            });
          }}
          aria-label={i18n.t('authenticated.invoices:new.selectAll')}
        />
      </label>
    ),
    cell: ({ row }) => {
      if (isAddOverheadSubRow(row)) return null;

      const disabled = isRowDisabled(row);

      return (
        <label
          onClick={event => event.stopPropagation()}
          className={cn(
            'flex cursor-pointer items-center justify-center py-4',
            disabled && DISABLED_ROW_CLASS
          )}
        >
          {isSubRow(row) ? (
            <Checkbox
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect() || hasQuickBooksError(row.getParentRow())}
              onCheckedChange={value => row.toggleSelected(!!value)}
              aria-label={i18n.t('authenticated.invoices:new.selectAll')}
            />
          ) : (
            <Checkbox
              checked={
                row.getIsAllSubRowsSelected()
                  ? true
                  : row.getIsSomeSelected()
                    ? 'indeterminate'
                    : false
              }
              disabled={!row.getCanSelect() || hasQuickBooksError(row) || !hasSubRows(row)}
              className="w-4"
              onCheckedChange={value => {
                row.subRows.forEach(subRow => {
                  // if checkbox indeterminate unsellect all subRows
                  if (row.getIsSomeSelected()) {
                    subRow.toggleSelected(false);
                    return;
                  }
                  if (!isAddOverheadSubRow(subRow)) {
                    subRow.toggleSelected(!!value);
                  }
                });
              }}
              aria-label={i18n.t('employeesTable.selectRow')}
            />
          )}
        </label>
      );
    },
    size: 40,
    minSize: 40,
    maxSize: 40,
    enableSorting: false,
    enableColumnFilter: false,
    enablePinning: true,
  }),
  columnHelper.accessor(row => row.project?.name, {
    id: 'project',
    header: ({ table }) => (
      <span className="flex items-center gap-1">
        <ExpandAllRowsButton table={table} />
        {i18n.t('authenticated.invoices:new.headers.projectAndMember')}
      </span>
    ),
    size: 350,
    minSize: 300,
    meta: {
      cellClassName: 'pl-0',
      title: i18n.t('authenticated.invoices:new.headers.projectAndMember'),
    },
    cell: ({ row, getValue }) => {
      if (isAddOverheadSubRow(row)) {
        if (hasQuickBooksError(row.getParentRow())) return null;
        return (
          <AddOverheadButton
            row={row}
            overheadActions={overheadActions}
            addedMemberActions={addedMemberActions}
          />
        );
      }

      if (isAddedMemberSubRow(row)) {
        const addedMember = row.original as unknown as GenerateInvoiceAddedMemberListItem;
        const parentRow = row.getParentRow();

        // Deliberately NOT excluding already-added members: the same person may legitimately
        // appear on one invoice several times at different rates (e.g. standard vs overtime
        // work in the same period). Rows are keyed by their own UUID, so duplicates get
        // distinct row ids and form paths.
        return (
          <div className="flex min-w-0 items-center gap-2 pl-10 pt-1">
            <div className="min-w-0 flex-1">
              <SingleSelectWithPagination
                name={getInvoiceFieldName(row, 'memberId')}
                control={form.control}
                disabled={isReadOnly(row)}
                showAvatars
                queryKey={ADDED_MEMBER_SELECTOR_QUERY_KEY}
                loadItems={addedMemberSelector.loadItems}
                searchItems={addedMemberSelector.searchItems}
                renderOption={renderMemberOption}
                renderTrigger={renderMemberTrigger}
                placeholder={i18n.t('authenticated.invoices:new.addedMember.selectPlaceholder')}
                isClearSelectionEnabled={false}
              />
            </div>
            <Button
              variant="ghost"
              size="xxs"
              className="shrink-0 text-muted-foreground hover:text-destructive"
              onClick={() =>
                parentRow &&
                addedMemberActions.removeAddedMember(parentRow.original.id, addedMember.id)
              }
            >
              <Trash2Icon size={14} />
            </Button>
          </div>
        );
      }

      if (isOverheadSubRow(row)) {
        const overhead = row.original as unknown as GenerateInvoiceProjectOverheadListItem;
        const parentRow = row.getParentRow();
        const isScheduled = overhead.overheadId !== undefined;
        const disabled = isRowDisabled(row);

        // Scheduled rows carry a server amount — seed it into the form once so
        // the rate/total cells display it (ad-hoc rows intentionally start empty).
        // Same first-render seeding pattern as the member invoicedBy cell below.
        const rateFieldName = getInvoiceFieldName(row, 'effectiveRate');
        if (isScheduled && form.getValues(rateFieldName) === undefined) {
          form.setValue(rateFieldName, overhead.effectiveRate, {
            shouldValidate: false,
            shouldDirty: false,
          });
        }

        return (
          <div
            className={cn(
              'flex min-w-0 items-center gap-2 pl-10 pt-1',
              disabled && DISABLED_ROW_CLASS
            )}
          >
            <Controller
              control={form.control}
              name={getInvoiceFieldName(row, 'description')}
              defaultValue={overhead.description}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder={i18n.t('authenticated.invoices:new.overhead.descriptionPlaceholder')}
                  className="min-w-0 flex-1 text-sm"
                  maxLength={FINANCE_LINE_DESCRIPTION_MAX_LENGTH}
                  // Mirrors the member cells: editable only while the row is
                  // selected — which also covers rows under projects without
                  // the QuickBooks connection (they can never be selected).
                  disabled={isReadOnly(row)}
                />
              )}
            />
            {isScheduled ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className="shrink-0 cursor-default gap-1 border-primary/40 text-primary"
                  >
                    <CalendarClockIcon size={12} />
                    {i18n.t('authenticated.invoices:new.overhead.scheduled')}
                  </Badge>
                </TooltipTrigger>
                <TooltipPortal>
                  <TooltipContent className="max-w-72">
                    {i18n.t('authenticated.invoices:new.overhead.scheduledTooltip', {
                      date: overhead.date ? formatDisplayDate(parseISO(overhead.date)) : '',
                    })}{' '}
                    {row.getIsSelected()
                      ? i18n.t('authenticated.invoices:new.overhead.scheduledTooltipDeselectHint')
                      : i18n.t('authenticated.invoices:new.overhead.scheduledTooltipSelectHint')}
                  </TooltipContent>
                </TooltipPortal>
              </Tooltip>
            ) : (
              <Button
                variant="ghost"
                size="xxs"
                className="shrink-0 text-muted-foreground hover:text-destructive"
                onClick={() =>
                  parentRow && overheadActions.removeOverhead(parentRow.original.id, overhead.id)
                }
              >
                <Trash2Icon size={14} />
              </Button>
            )}
          </div>
        );
      }

      if (isMemberSubRow(row)) {
        const original = row.original as unknown as GenerateInvoiceProjectTeamMemberListItem;
        const { name, avatarUrl } = original;

        return (
          <div
            className={cn(
              'flex min-w-0 items-center pl-10 pt-1',
              isRowDisabled(row) && DISABLED_ROW_CLASS
            )}
          >
            <ScrollableCellWithAvatar
              text={name}
              image={avatarUrl}
              avatarName={name}
              alt={`${name} avatar`}
              className="min-w-0 flex-1"
            />
            <MemberZeroAmountError row={row} />
          </div>
        );
      }

      const canExpand = row.subRows?.length > 0;
      const disabled = isRowDisabled(row);
      const { billingModel, clientName, colorHex } = row.original.project;

      return (
        <div className="flex min-w-0 items-center gap-1">
          <div className="flex min-w-0 flex-1 items-center">
            <Button
              variant="ghost"
              onClick={row.getToggleExpandedHandler()}
              style={{
                cursor: 'pointer',
                border: 'none',
                background: 'none',
                visibility: canExpand ? 'visible' : 'hidden',
              }}
              size="xxs"
              className="mx-3 shrink-0 text-muted-foreground"
            >
              {row.getIsExpanded() ? <SquareMinusIcon size={16} /> : <SquarePlusIcon size={16} />}
            </Button>
            <ProjectCell
              projectName={getValue()}
              billingModel={billingModel}
              clientName={clientName}
              colorHex={colorHex}
              disabled={!!disabled}
            />
          </div>
          {!hasQuickBooksError(row) && paymentDetailsSelector && (
            <PaymentDetailsCell projectId={row.original.id} selector={paymentDetailsSelector} />
          )}
          <QuickBooksWarningsIndicator
            messages={buildQuickBooksWarningMessages(row.original.project)}
          />
          <ProjectZeroAmountWarning row={row} />
        </div>
      );
    },
  }),
  columnHelper.accessor('invoiceBy', {
    header: i18n.t('authenticated.invoices:new.headers.invoiceBy'),
    size: 200,
    minSize: 175,
    cell: ({ row, getValue }) => {
      if (isAddOverheadSubRow(row)) return null;

      if (isOverheadSubRow(row) || isAddedMemberSubRow(row)) {
        return (
          <span className="text-sm text-muted-foreground">{i18n.t('common:table.noValue')}</span>
        );
      }

      const disabled = isRowDisabled(row);

      if (isMemberSubRow(row)) {
        const original = row.original as unknown as GenerateInvoiceProjectTeamMemberListItem;

        const trackedSource = original.hoursSources.find(
          s => s.sourceType === InvoiceHoursSourceType.TRACKED_BILLABLE
        );

        const currentInvoicedBy = form.getValues(getInvoiceFieldName(row, 'invoicedBy'));
        if (!currentInvoicedBy && trackedSource) {
          form.setValue(getInvoiceFieldName(row, 'invoicedBy'), trackedSource.sourceType, {
            shouldValidate: false,
            shouldDirty: false,
          });
          form.setValue(getInvoiceFieldName(row, 'hours'), trackedSource.hours, {
            shouldValidate: false,
            shouldDirty: false,
          });
          form.setValue(getInvoiceFieldName(row, 'effectiveRate'), trackedSource.averageRate, {
            shouldValidate: false,
            shouldDirty: false,
          });
          form.setValue(getInvoiceFieldName(row, 'total'), trackedSource.amount, {
            shouldValidate: false,
            shouldDirty: false,
          });
        }

        const sourceOrder = [
          InvoiceHoursSourceType.TRACKED_BILLABLE,
          InvoiceHoursSourceType.TRACKED_TOTAL,
          InvoiceHoursSourceType.APPROVED_BILLABLE,
          InvoiceHoursSourceType.APPROVED_TOTAL,
          InvoiceHoursSourceType.BOOKED_BILLABLE,
          InvoiceHoursSourceType.BOOKED_TOTAL,
          InvoiceHoursSourceType.CAPACITY,
        ];
        const orderedSources = sourceOrder
          .map(sourceType => original.hoursSources.find(s => s.sourceType === sourceType))
          .filter((source): source is InvoiceHoursSource => source !== undefined);

        const options = orderedSources.map(source => {
          const labelKey = source.sourceType.toLowerCase();

          return {
            label: i18n.t(`authenticated.invoices:new.invoiceBy.${labelKey}`),
            value: source.sourceType,
            customContent: (
              <div className="flex w-full items-center justify-between">
                <span>{i18n.t(`authenticated.invoices:new.invoiceBy.${labelKey}`)}</span>
                <span>{i18n.t('common:timeFormat.hoursOnly', { hours: source.hours })}</span>
              </div>
            ),
          };
        });

        const timeOffSource = original.hoursSources.find(
          s => s.sourceType === InvoiceHoursSourceType.TIME_OFF
        );

        return (
          <div className={cn(disabled && DISABLED_ROW_CLASS)}>
            <SingleSelect
              options={options}
              name={getInvoiceFieldName(row, 'invoicedBy')}
              control={form.control}
              disabled={isReadOnly(row)}
              searchable={false}
              isClearSelectionEnabled={false}
              onChange={(value: InvoiceHoursSourceType) => {
                const selectedSource = original.hoursSources.find(s => s.sourceType === value);

                if (selectedSource) {
                  form.setValue(getInvoiceFieldName(row, 'hours'), selectedSource.hours, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });

                  form.setValue(
                    getInvoiceFieldName(row, 'effectiveRate'),
                    selectedSource.averageRate,
                    {
                      shouldValidate: true,
                      shouldDirty: true,
                    }
                  );

                  form.setValue(getInvoiceFieldName(row, 'total'), selectedSource.amount, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }
              }}
              renderTrigger={selectedOption => {
                if (selectedOption) {
                  return (
                    <div className="flex w-full items-center justify-start gap-2">
                      {selectedOption.label}
                    </div>
                  );
                }
              }}
            />
            {timeOffSource && timeOffSource.hours > 0 && (
              <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                <span>{i18n.t('authenticated.invoices:new.invoiceBy.timeOff')}</span>
                <span>
                  {i18n.t('common:timeFormat.hoursOnly', {
                    hours: formatTrimmedDecimal(timeOffSource.hours),
                  })}
                </span>
              </div>
            )}
          </div>
        );
      }

      return (
        <span className={cn(disabled && DISABLED_ROW_CLASS)}>
          {getValue() || i18n.t('common:table.noValue')}
        </span>
      );
    },
  }),
  columnHelper.accessor('hours', {
    header: i18n.t('authenticated.invoices:new.headers.totalHours'),
    size: 200,
    minSize: 150,
    cell: ({ row }) => {
      if (isAddOverheadSubRow(row)) return null;

      if (isOverheadSubRow(row)) {
        return (
          <span
            className={cn(
              'text-sm text-muted-foreground',
              isRowDisabled(row) && DISABLED_ROW_CLASS
            )}
          >
            {formatTrimmedDecimal(1)}
          </span>
        );
      }

      const disabled = isRowDisabled(row);

      if (isMemberSubRow(row) || isAddedMemberSubRow(row)) {
        return (
          <div className={cn(disabled && DISABLED_ROW_CLASS)}>
            <MemberHoursInput row={row} form={form} />
          </div>
        );
      }

      return (
        <div className={cn(disabled && DISABLED_ROW_CLASS)}>
          <ProjectTotalHours row={row} />
        </div>
      );
    },
  }),
  columnHelper.accessor('effectiveRate', {
    header: i18n.t('authenticated.invoices:new.headers.effectiveRate'),
    size: 250,
    minSize: 200,
    cell: ({ row }) => {
      if (isAddOverheadSubRow(row)) return null;

      if (isOverheadSubRow(row)) {
        return (
          <div className={cn(isRowDisabled(row) && DISABLED_ROW_CLASS)}>
            <InputFieldWithCurrency
              name={getInvoiceFieldName(row, 'effectiveRate')}
              isReadMode={!row.getIsSelected()}
              control={form.control}
              suffixText={
                (row.original as unknown as GenerateInvoiceProjectOverheadListItem).currency.code
              }
              maxDecimalPlaces={FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES}
              maxIntegerDigits={FINANCE_MAX_INTEGER_DIGITS}
              allowNegative
            />
          </div>
        );
      }

      const disabled = isRowDisabled(row);

      if (isMemberSubRow(row) || isAddedMemberSubRow(row)) {
        return (
          <div className={cn(disabled && DISABLED_ROW_CLASS)}>
            <MemberRateCell row={row} form={form} />
          </div>
        );
      }

      return (
        <div className={cn(disabled && DISABLED_ROW_CLASS)}>
          <ProjectAverageRate row={row} currencyCode={row.original.currency.code} />
        </div>
      );
    },
  }),
  columnHelper.display({
    id: 'total',
    header: () => i18n.t('authenticated.invoices:new.headers.invoiceTotal'),
    size: 250,
    minSize: 200,
    cell: ({ row }) => {
      if (isAddOverheadSubRow(row)) return null;

      if (isOverheadSubRow(row)) {
        return (
          <div className={cn(isRowDisabled(row) && DISABLED_ROW_CLASS)}>
            <OverheadTotal row={row} />
          </div>
        );
      }

      const disabled = isRowDisabled(row);

      if (isMemberSubRow(row)) {
        return (
          <div className={cn(disabled && DISABLED_ROW_CLASS)}>
            <MemberTotal row={row} currencyCode={row.original.currency.code} />
          </div>
        );
      }

      if (isAddedMemberSubRow(row)) {
        return (
          <div className={cn(disabled && DISABLED_ROW_CLASS)}>
            <MemberTotal
              row={row}
              currencyCode={
                (row.original as unknown as GenerateInvoiceAddedMemberListItem).currency.code
              }
            />
          </div>
        );
      }

      return (
        <div className={cn(disabled && DISABLED_ROW_CLASS)}>
          <ProjectTotalAmount row={row} currencyCode={row.original.currency.code} />
        </div>
      );
    },
  }),
];
